import type { GlanceDir } from '../sim/types';
import type { InputAction, InputSource, SourceSample } from './InputSource';
import { clutchReleaseStep } from './clutchProfile';

type SteeringSide = 'left' | 'right';

/**
 * Fuente multitáctil alimentada por los controles visibles del HUD.
 * Los pedales y el volante se suavizan igual que el teclado para evitar
 * cambios instantáneos poco realistas al tocar una pantalla.
 * El embrague es un deslizador ANALÓGICO: mientras el dedo está sobre él
 * manda su posición; al soltarlo, el pedal vuelve con la suelta graduada.
 */
export class TouchSource implements InputSource {
  private actions: InputAction[] = [];
  private throttleHeld = false;
  private brakeHeld = false;
  private steeringHeld: Record<SteeringSide, boolean> = { left: false, right: false };
  private glanceStack: GlanceDir[] = [];
  private throttle = 0;
  private brake = 0;
  private steer = 0;
  /** Posición del dedo en el deslizador de embrague (null = sin dedo). */
  private clutchFinger: number | null = null;
  private clutch = 0;

  setThrottle(held: boolean): void {
    this.throttleHeld = held;
  }

  setBrake(held: boolean): void {
    this.brakeHeld = held;
  }

  setSteering(side: SteeringSide, held: boolean): void {
    this.steeringHeld[side] = held;
  }

  /** Deslizador de embrague: valor 0..1 mientras se arrastra, null al soltar. */
  setClutch(value: number | null): void {
    this.clutchFinger = value === null ? null : Math.min(1, Math.max(0, value));
  }

  setGlance(dir: Exclude<GlanceDir, 'none'>, held: boolean): void {
    this.glanceStack = this.glanceStack.filter((item) => item !== dir);
    if (held) this.glanceStack.push(dir);
  }

  pushAction(action: InputAction): void {
    this.actions.push(action);
  }

  releaseAll(): void {
    this.throttleHeld = false;
    this.brakeHeld = false;
    this.steeringHeld.left = false;
    this.steeringHeld.right = false;
    this.glanceStack = [];
    this.clutchFinger = null;
  }

  sample(dt: number): SourceSample {
    this.throttle = this.throttleHeld
      ? Math.min(1, this.throttle + dt * 1.6)
      : Math.max(0, this.throttle - dt * 3.5);
    this.brake = this.brakeHeld
      ? Math.min(1, this.brake + dt * 2.6)
      : Math.max(0, this.brake - dt * 5);

    const target = (this.steeringHeld.right ? 1 : 0) - (this.steeringHeld.left ? 1 : 0);
    const rate = target === 0 ? 3.2 : 2.6;
    const delta = Math.min(dt * rate, Math.abs(target - this.steer));
    this.steer += Math.sign(target - this.steer) * delta;

    if (this.clutchFinger !== null) {
      // Sigue al dedo con rapidez (pisar es inmediato, soltar lo frena el perfil)
      const diff = this.clutchFinger - this.clutch;
      const maxStep = (diff > 0 ? 8 : 4) * dt;
      this.clutch += Math.sign(diff) * Math.min(Math.abs(diff), maxStep);
    } else if (this.clutch > 0) {
      this.clutch = clutchReleaseStep(this.clutch, dt);
    }

    return {
      throttle: this.throttle,
      brake: this.brake,
      steer: this.steer,
      glance: this.glanceStack[this.glanceStack.length - 1] ?? 'none',
      clutchAnalog: this.clutch,
      clutchHold: false,
      clutchFeed: false,
      clutchMomentary: false,
    };
  }

  drainActions(): InputAction[] {
    const out = this.actions;
    this.actions = [];
    return out;
  }

  dispose(): void {
    this.releaseAll();
    this.actions = [];
  }
}
